/**
 * Capacitor Bridge - iOS App Integration
 * Handles Capacitor-specific functionality for the iOS wrapper app.
 * This script runs on every page load to:
 * 1. Remove 'not-ready' class for UI visibility
 * 2. Add 'platform-capacitor' class when running in Capacitor
 * 3. Handle active states for capacitor toolbar navigation
 * 4. Intercept external links and open them in in-app browser
 */
(function() {
  'use strict';

  /**
   * Find parent element by tag name
   * @param {Element} element - Starting element
   * @param {string} tagName - Tag name to find
   * @returns {Element|null} - Found parent or null
   */
  function findParentByTagName(element, tagName) {
    let parent = element;
    const upperTagName = tagName.toUpperCase();

    while (parent !== null && parent.tagName !== upperTagName) {
      parent = parent.parentNode;
    }

    return parent;
  }

  /**
   * Initialize pull-to-refresh on share pages
   */
  function initPullToRefresh() {
    var container = document.querySelector('.share-container') || document.querySelector('my-projects');
    if (!container) return;

    // Inject pull-to-refresh styles once
    if (!document.getElementById('ptr-styles')) {
      var style = document.createElement('style');
      style.id = 'ptr-styles';
      style.textContent = '.pull-to-refresh-indicator{height:0;overflow:hidden;display:flex;align-items:center;justify-content:center;transition:height .2s ease,opacity .2s ease;opacity:0}.pull-to-refresh-indicator.visible{transition:none}.pull-to-refresh-spinner{width:24px;height:24px;border:3px solid #ccc;border-top-color:rgb(var(--color-button));border-radius:50%;display:block}.pull-to-refresh-indicator.ready .pull-to-refresh-spinner{transform:rotate(180deg)}.pull-to-refresh-indicator.refreshing .pull-to-refresh-spinner{animation:ptr-spin .6s linear infinite}@keyframes ptr-spin{to{transform:rotate(360deg)}}';
      document.head.appendChild(style);
    }

    var indicator = document.createElement('div');
    indicator.className = 'pull-to-refresh-indicator';
    indicator.innerHTML = '<span class="pull-to-refresh-spinner"></span>';
    container.parentNode.insertBefore(indicator, container);

    var startY = 0;
    var currentY = 0;
    var pulling = false;
    var threshold = 80;

    container.addEventListener('touchstart', function(e) {
      if (window.scrollY === 0) {
        startY = e.touches[0].clientY;
        pulling = true;
        indicator.classList.remove('refreshing');
      }
    }, { passive: true });

    container.addEventListener('touchmove', function(e) {
      if (!pulling) return;
      currentY = e.touches[0].clientY;
      var pullDistance = currentY - startY;

      if (pullDistance > 0 && window.scrollY === 0) {
        var distance = Math.min(pullDistance * 0.5, 120);
        indicator.style.height = distance + 'px';
        indicator.style.opacity = Math.min(distance / threshold, 1);
        indicator.classList.add('visible');

        if (distance >= threshold) {
          indicator.classList.add('ready');
        } else {
          indicator.classList.remove('ready');
        }
      }
    }, { passive: true });

    container.addEventListener('touchend', function() {
      if (!pulling) return;
      pulling = false;

      var pullDistance = (currentY - startY) * 0.5;
      if (pullDistance >= threshold) {
        indicator.classList.add('refreshing');
        indicator.classList.remove('ready');
        indicator.style.height = '50px';
        indicator.style.opacity = '1';
        window.location.reload();
      } else {
        indicator.style.height = '0';
        indicator.style.opacity = '0';
        indicator.classList.remove('visible', 'ready');
      }

      startY = 0;
      currentY = 0;
    }, { passive: true });
  }

  /**
   * Initialize Capacitor bridge functionality
   */
  function initCapacitorBridge() {
    // Remove not-ready class to show UI
    document.body.classList.remove('not-ready');

    // Check if running in Capacitor environment
    if (!window.Capacitor) {
      return;
    }

    // Add platform class for Capacitor-specific styling
    document.body.classList.add('platform-capacitor');
    const forceUpgradeModule = window.CapacitorForceUpgrade;
    const enforceUpgradePromise = forceUpgradeModule && typeof forceUpgradeModule.enforceIfNeeded === 'function'
      ? Promise.resolve(forceUpgradeModule.enforceIfNeeded())
      : Promise.resolve(false);

    enforceUpgradePromise.then(function(isBlockedByUpgrade) {
      if (isBlockedByUpgrade) {
        return;
      }

    // Set active state for current toolbar link
    const currentPath = location.pathname + location.search;
    const toolbarLinks = document.querySelectorAll('.capacitor-toolbar a[href="' + currentPath + '"]');
    toolbarLinks.forEach(function(link) {
      if (link.parentElement) {
        link.parentElement.classList.add('active');
      }
    });

    // Pull-to-refresh on share pages
    initPullToRefresh();

    // Intercept external link clicks to open in in-app browser
    document.addEventListener('click', function(event) {
      const anchor = findParentByTagName(event.target || event.srcElement, 'A');

      if (!anchor) {
        return;
      }

      const href = anchor.getAttribute('href');
      if (!href || href === '#') {
        return;
      }

      // Skip links handled by auth interception (login/register/logout flow)
      if (anchor.hasAttribute('data-web-login-link') || anchor.hasAttribute('data-web-register-link') || anchor.hasAttribute('data-web-logout-link')) {
        return;
      }

      // Intercept account page links (Orders, Profile, etc.) in native app.
      // Shopify new customer accounts live on a subdomain (account.shopifydev.mimeophotos.com)
      // which is in allowNavigation, so WKWebView handles it without opening Safari.
      const hrefLower = href.toLowerCase();
      if (hrefLower.includes('/account') && !hrefLower.includes('/account/login') && !hrefLower.includes('/logout')) {
        try {
          if (localStorage.getItem('auth_token')) {
            // User is authenticated — navigate the main WebView directly.
            // allowNavigation covers *.mimeophotos.com, *.shopify.com etc.
            event.preventDefault();
            event.stopImmediatePropagation();
            window.location.href = anchor.href;
            return;
          }
          // Not authenticated — go through full auth flow
          if (typeof window.redirectToAuthInit === 'function') {
            event.preventDefault();
            event.stopImmediatePropagation();
            window.redirectToAuthInit();
            return;
          }
        } catch (e) {}
      }

      // Skip non-http(s) schemes (mailto:, tel:, sms:, etc.) — let the OS handle them
      if (/^(mailto|tel|sms|facetime|geo):/i.test(href)) {
        return;
      }

      let url;
      try {
        url = new URL(href, location.href);
      } catch (err) {
        // Invalid URL, let it proceed normally
        return;
      }

      // Check if link is external (different host or protocol)
      // Allow known internal domains to navigate in main WebView
      const isInternalHost = url.host === location.host ||
                             url.host === 'cta-service-cms2.hubspot.com' ||
                             url.host.endsWith('.mimeophotos.com') ||
                             url.host.endsWith('.myshopify.com') ||
                             url.host.endsWith('.shopify.com') ||
                             url.host.endsWith('.shop.app');
      const isExternal = !isInternalHost || url.protocol !== location.protocol;

      if (isExternal) {
        event.preventDefault();
        event.stopImmediatePropagation();

        // Prefer Capacitor Browser plugin (works reliably with remote server.url)
        if (window.Capacitor && window.Capacitor.Plugins && window.Capacitor.Plugins.Browser) {
          window.Capacitor.Plugins.Browser.open({ url: anchor.href });
        } else if (window.cordova && window.cordova.InAppBrowser) {
          window.cordova.InAppBrowser.open(anchor.href, '_blank', 'location=no');
        } else {
          // Last resort: open in new window
          window.open(anchor.href, '_blank');
        }
      }
    }, false);
    }).catch(function() {
      // Fail open: keep app usable even if force upgrade check errors.
    });
  }

  // Run initialization when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCapacitorBridge);
  } else {
    // DOM is already ready
    initCapacitorBridge();
  }
})();
